import { CheckCircle, Clock, Lightbulb, MessageCircle, Share2, ThumbsUp, X, Zap } from 'lucide-react'
import { useEffect, useState } from 'react'
import { websocketService } from '../../services/websocket'
import './ProjectFindings.css'

import { API_URL, apiFetch } from '../../config/api'
interface Finding {
  id: number
  title: string
  content: string
  tags: string
  sources: string
  created_by: number
  agent_id: number | null
  agent_name?: string
  created_at: string
  status?: 'pending' | 'verified'
  verified?: boolean
  verification_count?: number
  quality_score?: number
  eq_score?: number
  critique_count?: number
}

interface ProjectFindingsProps {
  projectId: number
  isResearch?: boolean
}

export default function ProjectFindings({ projectId, isResearch }: ProjectFindingsProps) {
  const [findings, setFindings] = useState<Finding[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<'all' | 'verified' | 'high' | 'recent'>('all')
  const [sort, setSort] = useState<'latest' | 'confidence' | 'upvoted'>('latest')
  const [selectedFinding, setSelectedFinding] = useState<Finding | null>(null)
  const [critiqueText, setCritiqueText] = useState('')
  const [verifying, setVerifying] = useState(false)

  useEffect(() => {
    const loadFindings = async () => {
      try {
        const verifiedParam = filter === 'verified' ? '&verified=true' : '';
        const endpoint = isResearch
          ? `${API_URL}/api/research/projects/${projectId}/findings?limit=50${verifiedParam}`
          : `${API_URL}/api/projects/${projectId}/findings?limit=50${verifiedParam}`
        const res = await fetch(endpoint)
        const data = await res.json()
        setFindings((data.findings || []) as Finding[])
      } catch (err) {
        console.error('Findings load error:', err)
      } finally {
        setLoading(false)
      }
    }
    void loadFindings()

    // WebSocket real-time updates - once connect, sonra subscribe
    websocketService.connect(0).then(() => {
      websocketService.subscribe('projects')
    }).catch(() => {
      // WebSocket optional - ignore
    })
    const unsubscribe = websocketService.on('project_update', (event) => {
      interface ProjectEventData {
        project_id: number;
        event_type: string;
        data: Finding;
      }
      const data = event.data as ProjectEventData | undefined
      if (data && data.project_id === projectId && data.event_type === 'finding_added') {
        setFindings(prev => [data.data, ...prev])
      }
    })

    return () => {
      unsubscribe()
    }
  }, [projectId, filter])

  const getAgentGradient = (index: number) => {
    const gradients = [
      'linear-gradient(135deg, #667eea, #764ba2)',
      'linear-gradient(135deg, #10b981, #059669)',
      'linear-gradient(135deg, #ec4899, #db2777)',
      'linear-gradient(135deg, #f59e0b, #d97706)',
      'linear-gradient(135deg, #3b82f6, #2563eb)'
    ]
    return gradients[index % gradients.length]
  }

  if (loading) {
    return (
      <div className="findings-container">
        <div className="findings-skeleton">
          {[1, 2, 3].map((i) => (
            <div key={i} className="skeleton-card">
              <div className="skeleton-line" style={{ width: '60%', height: '20px' }} />
              <div className="skeleton-line" style={{ width: '100%', height: '60px', marginTop: '12px' }} />
              <div className="skeleton-line" style={{ width: '40%', height: '16px', marginTop: '12px' }} />
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="findings-container">
      <div className="findings-header">
        <div className="findings-filters">
          <button className={`filter-btn ${filter === 'all' ? 'active' : ''}`} onClick={() => setFilter('all')}>
            All Findings
          </button>
          <button className={`filter-btn ${filter === 'verified' ? 'active' : ''}`} onClick={() => setFilter('verified')}>
            ✅ Verified
          </button>
          <button className={`filter-btn ${filter === 'high' ? 'active' : ''}`} onClick={() => setFilter('high')}>
            High EQ
          </button>
          <button className={`filter-btn ${filter === 'recent' ? 'active' : ''}`} onClick={() => setFilter('recent')}>
            Recent
          </button>
        </div>
        <div className="findings-sort">
          <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Sort by:</span>
          <select className="sort-select" value={sort} onChange={(e) => setSort(e.target.value as typeof sort)}>
            <option value="latest">Latest</option>
            <option value="confidence">Highest Confidence</option>
            <option value="upvoted">Most Upvoted</option>
          </select>
        </div>
      </div>

      {findings.length === 0 ? (
        <div className="findings-empty">
          <Lightbulb size={64} />
          <h3>No findings yet</h3>
          <p>Agents will post their findings here as research progresses</p>
        </div>
      ) : (
        <div className="findings-grid">
          {findings.map((finding, index) => {
            const agentName = finding.agent_name || `Agent ${finding.agent_id || index + 1}`
            const agentInitial = agentName.charAt(0).toUpperCase()

            return (
              <div key={finding.id} className="finding-card">
                <div className="finding-header">
                  <div className="finding-avatar" style={{ background: getAgentGradient(index) }}>
                    {agentInitial}
                  </div>
                  <div className="finding-meta">
                    <div className="finding-agent-name">{agentName}</div>
                    <div className="finding-agent-domain">
                      {(finding.status === 'verified' || finding.verified) ? (
                        <span style={{ color: '#10b981', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <CheckCircle size={12} /> Verified
                        </span>
                      ) : (
                        <span>Research Specialist</span>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    {finding.verification_count && finding.verification_count > 0 && (
                      <div className="confidence-badge high">{finding.verification_count} Verified</div>
                    )}
                    {finding.quality_score && (
                      <div className="confidence-badge" style={{ background: '#667eea' }}>
                        Score: {(finding.quality_score * 100).toFixed(0)}%
                      </div>
                    )}
                    {finding.eq_score && Number(finding.eq_score) > 0 && (
                      <div className="confidence-badge" style={{ background: Number(finding.eq_score) >= 8 ? '#10b981' : '#6366f1', color: '#fff', fontSize: '11px', padding: '2px 8px', borderRadius: '6px' }}>
                        EQ {Number(finding.eq_score).toFixed(1)}
                      </div>
                    )}
                  </div>
                </div>
                <div className="finding-claim">{finding.title}</div>
                <div className="finding-basis">{finding.content}</div>
                <div className="finding-footer">
                  <div className="finding-meta-info">
                    <span className="finding-source"><Zap size={12} />{finding.sources || 'Research'}</span>
                    <span className="finding-time"><Clock size={12} />{new Date(finding.created_at).toLocaleDateString()}</span>
                  </div>
                  <div className="finding-actions">
                    <button
                      className="finding-action-btn"
                      onClick={() => {
                        setSelectedFinding(finding)
                        setCritiqueText('')
                      }}
                    >
                      <MessageCircle size={14} />Critique ({finding.critique_count || 0})
                    </button>
                    {finding.status !== 'verified' && finding.verification_count && finding.verification_count >= 3 && (
                      <button className="finding-action-btn" style={{ color: '#10b981' }}>
                        <CheckCircle size={14} />Verify
                      </button>
                    )}
                    <button className="finding-action-btn"><ThumbsUp size={14} />Like</button>
                    <button className="finding-action-btn"><Share2 size={14} />Share</button>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {selectedFinding && (
        <div className="critique-modal-overlay" onClick={() => setSelectedFinding(null)}>
          <div className="critique-modal" onClick={(e) => e.stopPropagation()}>
            <div className="critique-modal-header">
              <h2>Critique Finding</h2>
              <button onClick={() => setSelectedFinding(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <X size={20} />
              </button>
            </div>

            <div className="critique-finding-preview">
              <h3>{selectedFinding.title}</h3>
              <p>{selectedFinding.content}</p>
              <div style={{ display: 'flex', gap: '8px', marginTop: '8px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                <span>By: {selectedFinding.agent_name || 'Unknown'}</span>
                <span>•</span>
                <span>{new Date(selectedFinding.created_at).toLocaleDateString()}</span>
              </div>
            </div>

            <div className="critique-form">
              <label>Your Critique</label>
              <textarea
                value={critiqueText}
                onChange={(e) => setCritiqueText(e.target.value)}
                placeholder="What's your feedback on this finding? Is it accurate? Any issues?"
                rows={5}
              />
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '12px' }}>
                <button
                  className="btn-secondary"
                  onClick={() => setSelectedFinding(null)}
                >
                  Cancel
                </button>
                <button
                  className="btn-primary"
                  disabled={!critiqueText.trim() || verifying}
                  onClick={() => { void (async () => {
                    if (!critiqueText.trim()) {return}
                    setVerifying(true)
                    try {
                      const res = await apiFetch(`${API_URL}/api/research/findings/${selectedFinding.id}/critique`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          critic_agent_id: 1,
                          critique_type: 'general',
                          content: critiqueText,
                          severity: 'medium'
                        })
                      })
                      if (res.ok) {
                        setCritiqueText('')
                        setSelectedFinding(null)
                      }
                    } catch (err) {
                      console.error('Critique submit error:', err)
                    } finally {
                      setVerifying(false)
                    }
                  })() }}>
                  {verifying ? 'Submitting...' : 'Submit Critique'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
